<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-form
    ref="userform"
  >
    <v-container fluid>
      <v-row class="mb-1">
        <v-col>  
          <v-text-field
            v-if="showDbId"
            v-model="user.userDbId"
            data-testid="user-db-id-input"
            disabled
            :label="$t('Id')"
            readonly
            autcomplete="off"
            dense
          ></v-text-field>
          <!-- user info, name, password, etc -->
          <v-text-field
            ref="uid"
            v-model="user.userId"
            :label="$t('CloudUser') + '*'"
            dense            
            autocomplete="off"
            :disabled="user.userDbId > 0"
            :success="unameSuccess"
            :error-messages="userIdErrorMessages"
            :loading="unameLoading"
            :rules="[rules.required, rules.email]"
            data-test="user-id-field"
            data-testid="user-id-input"
            @blur="onUserEmailBlur"
            @input="onUserIdInput($event)"
          ></v-text-field>
        </v-col>
        <v-col
          v-if="showDbId"
          cols="auto"
        >
          <UserImageEdit
            v-if="showAvatar"
            :user-db-id="user.userDbId"
          />
        </v-col>
      </v-row>

      <v-text-field
        ref="uname"
        v-model="user.userName"
        data-testid="user-name-input"
        :label="$t('Name') + '*'"
        autcomplete="off"
        dense            
        :rules="[rules.required]"
        @input="onUserNameInput($event)"
      ></v-text-field>
 
      <div
        v-if="canSendSetPasswordEmail"
        class="mb-4"
      >
        <v-switch
          ref="sendSetPasswordEmail"
          v-model="sendSetPasswordEmail"
          :label="$t('SendSetPasswordEmail')"
          dense
          hide-details
          class="mt-0"
          data-test="send-set-password-email-switch"
          data-testid="user-send-set-password-email-switch"
          @change="onSendSetPasswordEmailChange"
        >
        </v-switch>
        <div class="send-set-password-email-hint text-caption">
          {{ $t('SendSetPasswordEmailHint') }}
        </div>
      </div>
      <!-- Editing an existing user: mail them a fresh link to set their own password.
           Sits next to the password fields because it is the alternative to typing one
           in here - typically used when the user's first link expired before they
           got round to it. -->
      <v-btn
        v-if="canResendSetPasswordEmail"
        text
        small
        color="primary"
        class="px-0 mb-2"
        :loading="resendingSetPasswordEmail"
        data-test="resend-set-password-email-button"
        data-testid="user-resend-set-password-email-button"
        @click.prevent="$emit('resend-set-password-email')"
      >
        <v-icon
          small
          left
        >
          far fa-envelope
        </v-icon>
        {{ $t('ResendSetPasswordEmail') }}
      </v-btn>
      <HdPasswordFields
        v-if="!sendSetPasswordEmail"
        ref="passwordField"
        :password-policy="passwordPolicyId"
        :can-be-empty="passwordCanBeEmpty"
        :disabled="isDisabledForExternalUser"
        test-id="user-password"
        @validation-changed="$emit('validation-changed')"
      />
      <v-text-field
        v-model="user.phoneNumber"
        data-testid="user-phone-input"
        :label="$t('Phone')"
        :rules="[rules.phone]"
        dense            
        @input="$emit('update:phoneNumber', $event)"
      ></v-text-field>

      <v-switch   
        ref="active"
        v-model="user.active"
        data-testid="user-active-switch"
        :label="$t('Active')"
        dense
        @input="$emit('update:active', $event)"
      >
      </v-switch>
    </v-container>
  </v-form>   
</template>
<script>
import { User } from '../../../model/user/User'
import { groupValue, groupId, hiddenCompaniesId } from '../../../util/enum'
import HdPasswordFields from '../field/HdPasswordFields'
import UserImageEdit from './UserImageEdit'

export default {
  name: 'HdCreateEditUserForm',
  components: {
    UserImageEdit,
    HdPasswordFields,
  },
  props: {
    /**
     * User object
     */
    user: {
      type: User,
      required: false,
      default() {
        return new User( { active: true } )
      },
    },
    /**
     * password policy to use
     */
    passwordPolicyId: {
      type: Number,
      required: true,
    },
    allowEmptyPassword: {
      type: Boolean,
      required: false,
      default() { 
        return false
      }
    },        
    showDbId: {
      type: Boolean,
      required: false,
      default() { 
        return true
      }
    },
    showAvatar: {
      type: Boolean,
      required: false,
      default() {
        return true
      }
    },
    /**
     * Offer the "send set-password email" toggle. Only makes sense when creating a
     * user - an existing account already has a password.
     */
    canSendSetPasswordEmail: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    /**
     * Offer the "resend set-password email" action. The mirror of
     * canSendSetPasswordEmail: only makes sense on an existing user, who may need a
     * new link if the first one expired.
     */
    canResendSetPasswordEmail: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    /**
     * Spinner state for the resend button, owned by the parent that calls the API.
     */
    resendingSetPasswordEmail: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    }

  },
  data() {
    return {
      sendSetPasswordEmail: false,
      unameError: false,
      // Message pushed in by the parent when the API rejects the user id, e.g. the 409 the
      // backend answers with when the account already exists. Shown under the field rather
      // than in a modal, so it reads like the client side validation errors next to it.
      userIdApiError: '',
      unameSuccess: false,
      unameLoading: false,
      validFields: false,
      rules: {
        phone: v => {
          const pattern = /^[\d*#+\-() ]+$/ //Numbers and specific special characters allowed
          return (!v || pattern.test(v)) || this.$t('PhoneError')
        },
        email: v => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z,_\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(v) || this.$t('EmailAddressError')
        },
        required: v => !!v || ' '
      },
    }
  },
  computed: {
    /**
     * Duplicate user ids come from two places - the availability check on blur and the
     * 409 the API answers with on save - and both say the same thing to the user.
     */
    userIdErrorMessages() {
      if (this.userIdApiError) {
        return [this.userIdApiError]
      }
      return this.unameError ? [this.$t('UserIdInUse')] : []
    },

    /**
     * With the toggle on the user sets their own password through the emailed link,
     * so no password is collected here at all.
     */
    passwordCanBeEmpty() {
      return this.allowEmptyPassword || this.sendSetPasswordEmail
    },

    isExternalUser() {
      return this.user?.company?.companyId === 4624 || // External company ID
             hiddenCompaniesId.includes(this.user?.company?.companyId) ||
             this.user?.group?.groupId === groupId.PortalExternalUser
    },

    isDisabledForExternalUser() {
      // Disable if:
      // 1. Current user is less than PartnerAdmin AND
      // 2. Target user is external
      return this.$accesscontrol().groupValue < groupValue.PartnerAdmin && 
             this.isExternalUser
    },

    canModifyTwoFactor() {
      return this.$accesscontrol().groupValue >= groupValue.PartnerAdmin ||
             !this.isExternalUser
    }
  },

  mounted() {
    // Store original group ID when component is created
    this.originalGroupId = this.user?.groupId
  },
  methods: {
    validate() {
      // Add validation to prevent unauthorized changes
      if (this.isDisabledForExternalUser) {
        // Check if password was attempted to be changed
        const passwordValue = this.$refs.passwordField?.getValue()
        if (passwordValue) {
          this.$alert({ 
            message: this.$t('NotAuthorizedToChangeExternalUserPassword'), 
            type: 'error' 
          })
          return false
        }

        // Check if role/group was attempted to be changed
        if (this.user.groupId !== this.originalGroupId) {
          this.$alert({ 
            message: this.$t('NotAuthorizedToChangeExternalUserRole'), 
            type: 'error' 
          })
          return false
        }
      }

      // Block validation if duplicate email error exists
      if (this.unameError || this.userIdApiError) {
        return false
      }

      // Password is mandatory unless it can be empty or user is external
      if (!this.isDisabledForExternalUser && !this.passwordCanBeEmpty) {
        const passwordValue = this.$refs.passwordField?.getValue()
        if (!passwordValue) {
          return false
        }
      }

      // Trigger password validation to show error state if needed
      if (!this.isDisabledForExternalUser) {
        this.$refs.passwordField?.validate()
      }

      let formValid = this.$refs.userform.validate()
      // The password fields are not rendered while the set-password email is toggled on
      let passwordValid = this.$refs.passwordField ? this.$refs.passwordField.validate() : true
      return formValid && passwordValid
    },

    getValues() {
      const values = {
        password: this.isDisabledForExternalUser || this.sendSetPasswordEmail ? '' : this.$refs.passwordField?.getValue(),
        sendSetPasswordEmail: this.canSendSetPasswordEmail ? this.sendSetPasswordEmail : undefined,
      }

      // Ensure group ID remains unchanged for external users if user lacks permission
      if (this.isDisabledForExternalUser) {
        values.groupId = this.originalGroupId
      }

      return values
    },

    updatedData(data) {
      // Store original group ID for comparison
      this.originalGroupId = data.groupId

      // Only update sensitive data if user has permission
      if (!this.isDisabledForExternalUser) {
        this.$refs.passwordField?.clear()
      }
    },

    /**
     * Show a server side error, such as a 409 "already exists", under the user id field.
     * @param {String} message
     */
    setUserIdError(message) {
      this.userIdApiError = message || ''
      this.unameSuccess = false
      this.$nextTick(() => {
        this.$emit('validation-changed')
      })
    },

    clearUserIdError() {
      if (!this.userIdApiError) {
        return
      }
      this.userIdApiError = ''
      this.$nextTick(() => {
        this.$emit('validation-changed')
      })
    },

    onSendSetPasswordEmailChange() {
      // Drop anything already typed so it can never be submitted, then re-check validity
      // now that the password requirement changed.
      this.$refs.passwordField?.clear()
      this.$nextTick(() => {
        this.$emit('validation-changed')
      })
    },

    onUserIdInput(event) {
      // The id just changed, so whatever the API said about the previous one no longer applies
      this.userIdApiError = ''
      this.unameError = false
      this.unameSuccess = false
      this.$emit('update:userId', event)
      this.$emit('validation-changed')
    },
    onUserNameInput(event) {
      this.$emit('update:userName', event)
      this.$emit('validation-changed')
    },
    onUserEmailBlur(e) {   
      if (this.user.userDbId > 0 || this.isDisabledForExternalUser) {
        return
      }
      const emailValue = e.target.value?.trim()
      if (!emailValue) {
        return
      }
      this.unameLoading = true
      User.isUserIdAvailable(emailValue)
        .then((response) => {          
          if (!response.data) {
            this.unameError = false
            this.unameSuccess = true
          } else {
            this.unameSuccess = false
            this.unameError = true        
          }
          this.$nextTick(() => {
            this.$emit('validation-changed')
          })
        })
        .catch(() => {
          this.unameError = false
          this.unameSuccess = false
        })
        .finally(() => {
          this.unameLoading = false
        })      
    },

    clear() {
      this.userIdApiError = ''
      this.unameError = false
      this.unameSuccess = false
      this.sendSetPasswordEmail = false
      if (!this.isDisabledForExternalUser) {
        this.$refs.passwordField?.clear()
      }
    }
  }
}
</script>
<style scoped>
/* Matches the muted look of a Vuetify hint, in both the light and the dark theme */
.send-set-password-email-hint {
  opacity: 0.7;
  line-height: 1.2;
}
</style>